(ns storrito.image
  (:require [vire.adserver.config :as cfg]
            [cljs-http.client :as http]
            [cljs.core.async :refer [<!] :as async]
            [clojure.string :as str]
            [goog.crypt.base64 :as base64]
            [sv.core.async :refer-macros [<?]])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(defn url
  "Builds an URL to download the image for the given `blob-id`."
  [blob-id]
  (str (cfg/server) "/image/" blob-id))

(defn file-uri
  [file]
  (js/window.URL.createObjectURL file))

(defn image-blob [blob-id]
  (go
    (let [resp (<! (http/get (url blob-id)
                             {:with-credentials? false
                              :response-type :blob}))]
      (when (= (:status resp) 200)
        (:body resp)))))

(defn load-image
  [blob-or-uri]
  (let [blob? (instance? js/Blob blob-or-uri)
        error-ch (async/promise-chan)
        image (doto (js/Image.)
                (.addEventListener "load"
                                   (fn [e] (async/close! error-ch)))
                (.addEventListener "error"
                                   (fn [e]
                                     (->> (ex-info "Failed to load image"
                                                   e)
                                          (async/put! error-ch)))))]
    (set! (.-src image)
          (if blob?
            (js/window.URL.createObjectURL blob-or-uri)
            blob-or-uri))
    (go
      (try
        (<? error-ch)
        image
        (catch js/Error e e)))))

(def +valid-image-endings+ #{"jpg" "jpeg" "png"})

(defn known-file-extension?
  [file]
  (some->> (str/split file.name ".")
           (last)
           (contains? +valid-image-endings+)))

;;; various fitting and scaling routines

(defn fill-factor
  "Returns a scale factor so that when an image with `size-a` is drawn
  at 0,0 it will fill a rect given by `size-b` (and possibly stretch
  outside its bounds if it doesn't have the same aspect ratio)"
  [size-a size-b]
  (let [width-ratio  (/ (:width size-b) (:width size-a))
        height-ratio (/ (:height size-b) (:height size-a))]
    (min width-ratio height-ratio)))

(defn fit-factor
  "Returns a scale factor so that when an image with `size-a` is drawn
  at 0,0 it will fit in a rect given by `size-b`"
  [size-a size-b]
  (let [width-ratio  (/ (:width size-b) (:width size-a))
        height-ratio (/ (:height size-b) (:height size-a))]
    (min 1.0 width-ratio height-ratio)))

(defn fill-factor-background
  "Returns a scale factor so that the scaled image would totally cover the story."
  [size-a size-b]
  (let [width-ratio  (/ (:width size-b) (:width size-a))
        height-ratio (/ (:height size-b) (:height size-a))]
    (*
      (max 1.0 width-ratio height-ratio)
      1.1)))

(defn scale [{:keys [width height]} scale-factor]
  {:pre [(number? width)
         (number? height)
         (not (js/isNaN scale-factor))]}
  {:width  (* width  scale-factor)
   :height (* height scale-factor)
   :scale-factor scale-factor})

(defn fit
  "Returns a new size with the same aspect ratio of `size-a` that will
  fit completely inside `size-b` when drawn at 0,0."
  [size-a size-b]
  (->> (fit-factor size-a size-b)
       (scale size-a)))

(defn fill-background [size-a size-b]
  (->> (fill-factor-background size-a size-b)
       (scale size-a)))

(defn fill [size-a size-b]
  (->> (fill-factor size-a size-b)
       (scale size-a)))

;;; Functions to modify images

(defn dimensions [img]
  {:width  img.width
   :height img.height})

(defn make-canvas [{:keys [width height]}]
  (let [canvas (js/document.createElement "canvas")]
    (set! (.-width canvas)  width)
    (set! (.-height canvas) height)
    canvas))

(defn canvas-dimensions [canvas]
  {:width  canvas.width
   :height canvas.height})

(defn data-url-to-blob
  "Converts a base64 data url into a blob."
  [data-url]
  (let [[_ content-type base64-str] (re-find #"data:(.*?);base64,(.*)" data-url)]
    (js/Blob. #js[(base64/decodeStringToUint8Array base64-str)] #js{:type content-type})))

(defn canvas-blob
  ([canvas {:keys [quality mime-type]}]
   (let [c (async/promise-chan)]
     (if (exists? (.-toBlob canvas))
       (.toBlob canvas
         (partial async/put! c)
         mime-type
         quality)

       (async/put! c
         (data-url-to-blob
           (.toDataURL canvas
             mime-type
             quality))))
     c))
  ([canvas]
   (canvas-blob canvas {:quality 100
                        :mime-type "image/jpeg"})))

(defn canvas-data-uri
  ([canvas {:keys [quality mime-type]}]
   (go (.toDataURL canvas mime-type quality)))
  ([canvas]
   (canvas-data-uri canvas {:quality 100
                            :mime-type "image/jpeg"})))

(defn background-color [canvas color]
  (let [ctx (.getContext canvas "2d")]
    (set! (.-fillStyle ctx) color)
    (.fillRect ctx 0 0 canvas.width canvas.height))
  canvas)

(defn draw-blurry-background
  [canvas image]
  (try
    (let [ctx-2d (.getContext canvas "2d")
          canvas-dimenstions (canvas-dimensions canvas)
          size (fill-background (dimensions image)
                                canvas-dimenstions)]
      (assert (instance? js/Image image))
      (set! (.-filter ctx-2d) "blur(128px)")
      (doto ctx-2d
        (.drawImage image
                    (/ (- canvas.width (:width size)) 2.0)
                    (/ (- canvas.height (:height size)) 2.0)
                    (:width size) (:height size))))
    (catch :default e
      ;; Catching this error because adding the blurry background is not necessary.
      (js/console.error e)))
  canvas)

(defn draw-image [canvas image [x y] {:keys [width height]}]
  (assert (instance? js/Image image))
  (set! (.-filter (.getContext canvas "2d")) "none")
  (doto (.getContext canvas "2d")
    (.drawImage image x y width height))
  canvas)

(defn draw-image-constrained
  "Draws `image` in `canvas` at 0,0 with a size given by
  `constrain-fn`. `constrain-fn` is a function given two arguments:
  The size of the image and the size of the canvas. It must return a
  new size."
  [canvas image constrain-fn]
  (let [canvas-dimensions (canvas-dimensions canvas)
        size (constrain-fn (dimensions image)
                           canvas-dimensions)
        centered-x (/ (- (:width canvas-dimensions)
                         (:width size))
                      2.0)
        centered-y (/ (- (:height canvas-dimensions)
                         (:height size))
                      2.0)]
    (draw-image canvas image [centered-x centered-y] size)))
